import statsData from '@/data/stats.json';
import resultsData from '@/data/results.json';

/**
 * Source unique des chiffres du club.
 *
 * Ils étaient auparavant recopiés dans chaque page, ce qui avait fini par
 * produire des contradictions visibles : « 1000+ élèves » sur l'accueil et
 * « plus de 500 » sur /join. Toute valeur affichée à plusieurs endroits passe
 * désormais par ici.
 */
export const STATS = statsData;

export interface ParsedStat {
  number: number;
  suffix: string;
}

/**
 * Décompose une valeur d'affichage en nombre et suffixe, pour les compteurs
 * animés du hero : « 1000+ » -> { number: 1000, suffix: '+' }.
 */
export function parseStat(value: string): ParsedStat {
  const match = value.match(/^([\d.]+)\s*(.*)$/);
  if (!match) return { number: 0, suffix: value };
  return { number: Number(match[1]), suffix: match[2] };
}

/**
 * Taux de réussite affiché hors de la section détaillée.
 *
 * Priorité au taux global de `results.json` (`overallRate`) : tous examens
 * confondus, calculé par la direction académique sur les effectifs réels.
 * Le site ne détient pas ces effectifs (le club a choisi de ne pas les
 * publier), donc ce chiffre est saisi et sourcé, pas recalculé ici.
 *
 * Sans taux global, repli sur le plus bas des taux d'examens affichés : un
 * minimum reste vrai quoi qu'il arrive, alors qu'une moyenne d'examens
 * différents sans leurs effectifs n'aurait aucun sens. C'est ce repli qui a
 * remplacé un « 95 % » écrit à la main qui contredisait les résultats.
 *
 * Le détail par série (`exam.classes`) est volontairement ignoré : une série
 * est un sous-groupe d'un examen, pas un examen.
 */
function computeSuccessRate(): { value: string; label: string } {
  // Daté : ne prétend rien sur les sessions précédentes.
  const label = `Taux de réussite ${resultsData.session}`;

  // Accès en optionnel : le type est déduit du JSON, et supprimer la ligne
  // `overallRate` du fichier doit faire basculer sur le repli, pas casser le
  // build. Une valeur vide ("") produit la même bascule.
  const overall = (resultsData as { overallRate?: string }).overallRate;
  if (overall && parseStat(overall).number > 0) {
    return { value: overall, label };
  }

  const rates = resultsData.exams
    .map((exam) => parseStat(exam.rate).number)
    .filter((n) => Number.isFinite(n) && n > 0);

  // Sans résultat exploitable, mieux vaut ne rien affirmer.
  if (rates.length === 0) {
    return { value: '', label: 'Taux de réussite' };
  }

  return { value: `${Math.min(...rates)} %`, label };
}

export const SUCCESS_RATE = computeSuccessRate();
